import React, { useState, useEffect } from 'react';
import _ from 'underscore';
import { titleize } from 'underscore.string';
import PropTypes from 'prop-types';
import InvoiceDetails from 'components/modules/accounting/invoices/invoice-details';
import CargomaticModal from 'components/v2/modal';
import { getInvoicesForShipment } from 'utils/AccountingWebAPIUtils';
import CircleCheckIcon from 'components/v2/icons/CircleCheckIcon';
import Tooltip from 'components/v2/Tooltip';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import SessionStore from 'stores/SessionStore';
import { canShowDeleteAction } from 'helpers/accounting';
import formatMoney from 'utils/formatters/Money';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import AddBaseCostForm from './AddBaseCostForm';
import AdjustmentTitle from './AdjustmentTitle';
import AdjustmentAction from './AdjustmentAction';
import DateField from './DateField';
import { sluggify } from '../../../../utils/localization/utils';
import ExternalLink from '../../../v2/ExternalLink';
import { fetchDocuments, handleDownloadRateCard } from 'utils/documentUtils';
import { Card, CardContent, CardHeader, CardTitle, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from '@cargomatic/ui';
import { showToast } from 'stores/ToastStore';
const shouldRenderCreateCreditMemoButton = (invoice, render = false) => {
    if (!invoice || !render)
        return false;
    const negativeItems = _.flatten(invoice === null || invoice === void 0 ? void 0 : invoice.lineItemGroups.map((lineItemGroup) => lineItemGroup === null || lineItemGroup === void 0 ? void 0 : lineItemGroup.lineItems.filter((lineItem) => (lineItem === null || lineItem === void 0 ? void 0 : lineItem.value) < 0)));
    if (negativeItems.length)
        return false;
    return true;
};
export default function ShipperCharges({ shipmentId, shipmentIsCurrent, isIntermodalShipmentType, charges, timezone, total, currency, onAddCustomAdjustment, onAddShipperCostAdjustment, onAddInternalAdjustment, onAddCustomerAdjustment, onAddBaseCost, onRemoveLineItem, shipperId, showDownloadRateCard, }) {
    var _a;
    const { t } = useTranslation();
    const [lineItems, setLineItems] = useState([]);
    const [selectedLineItem, setSelectedLineItem] = useState();
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showConfirmRemovalModal, setShowConfirmRemovalModal] = useState(false);
    const [recentRateCard, setRecentRateCard] = useState({});
    function handleHideDetailsModal() {
        setShowDetailsModal(false);
    }
    async function getInvoices() {
        try {
            const { entity: { invoices }, } = await getInvoicesForShipment(shipmentId);
            const creditMemos = invoices.filter((i) => i.original_invoice);
            const mappedLineItems = charges.map((lineItem) => {
                var _a;
                const invoice = invoices.find((p) => p._id === lineItem.invoice_id);
                const hasCreditMemo = creditMemos.find((p) => p.original_invoice === lineItem.invoice_id);
                const isFirstFuelSurchargeItem = ((_a = charges.find((c) => c.charge_code === 'FSC')) === null || _a === void 0 ? void 0 : _a._id) === lineItem._id;
                let isCreditMemo = false;
                if (invoice &&
                    invoice.invoice_type &&
                    invoice.invoice_type.toLowerCase() === 'credit') {
                    isCreditMemo = true;
                }
                if (!invoice) {
                    return Object.assign(Object.assign({}, lineItem), { hasInvoice: false, hasCreditMemo: false, isCreditMemo, invoice: null, renderCreditMemoActionButton: false, isFirstFuelSurchargeItem });
                }
                const { _id, number, timestamp, status, email_sent, edi_sent, created_by, original_invoice, } = invoice;
                return Object.assign(Object.assign({}, lineItem), { hasInvoice: true, hasCreditMemo: !!hasCreditMemo, isCreditMemo, invoice: {
                        _id,
                        number,
                        timestamp,
                        status,
                        email_sent,
                        edi_sent,
                        created_by,
                        original_invoice,
                    }, renderCreditMemoActionButton: shouldRenderCreateCreditMemoButton(invoice, !isCreditMemo && !hasCreditMemo), isFirstFuelSurchargeItem });
            });
            setLineItems(mappedLineItems);
        }
        catch (e) {
            console.log('Error getting invoices', e);
        }
    }
    function handleRemove(lineItem) {
        return () => {
            setShowConfirmRemovalModal(true);
            setSelectedLineItem(lineItem);
        };
    }
    async function handleConfirmLineItemRemoval() {
        await onRemoveLineItem(selectedLineItem, 'receivables');
        handleHideConfirmRemovalModal();
    }
    function handleHideConfirmRemovalModal() {
        setShowConfirmRemovalModal(false);
        setSelectedLineItem(null);
    }
    const isFinance = SessionStore.isFinance() || SessionStore.isFinanceAdmin();
    function showRemoveButton(lineItem) {
        return canShowDeleteAction(lineItem, isFinance, isIntermodalShipmentType);
    }
    function renderRemoveButton(lineItem) {
        if (showRemoveButton(lineItem)) {
            return (React.createElement(DeleteButton, { dataTestId: "DeleteButton-aee6fb8a", disabled: lineItem.processed, onClick: handleRemove(lineItem) }));
        }
    }
    function renderInvoiceLink(lineItem) {
        if (!lineItem.invoice) {
            return null;
        }
        const { invoice: { _id, number }, } = lineItem;
        return (React.createElement(ExternalLink, { dataTestId: "ExternalLink-300399a1", href: `/invoices/${_id}`, title: number, showIcon: true }));
    }
    const renderDownloadLink = () => {
        if (recentRateCard === null || recentRateCard === void 0 ? void 0 : recentRateCard.pdfUrl) {
            return (React.createElement("a", { onClick: () => handleDownloadRateCard(recentRateCard === null || recentRateCard === void 0 ? void 0 : recentRateCard.pdfUrl, true), className: "underline" }, t('LATEST_SHIPPER_RATE_CARD')));
        }
        return React.createElement("p", { className: "text-red" }, t('NO_RATE_CARD_GENERATED'));
    };
    // Bens recommendation for accessorials beyond the 2 week period.
    // Some might be incorrectly marked but it was a trade off decision
    function getRateSource(lineItem) {
        if (!(lineItem === null || lineItem === void 0 ? void 0 : lineItem.rate_source)) {
            return (lineItem === null || lineItem === void 0 ? void 0 : lineItem.autorated) ? t('AUTOMATED') : t('MANUAL');
        }
        return t(sluggify(lineItem.rate_source));
    }
    async function getRateCard() {
        try {
            const rateCardData = await fetchDocuments(shipperId, 'customer_rate_card');
            setRecentRateCard(rateCardData);
        }
        catch (err) {
            console.error(err);
            showToast('', t('FAILED_TO_DOWNLOAD_RATE_CARD'), 'error');
        }
    }
    useEffect(() => {
        getInvoices();
        getRateCard();
    }, [charges]);
    return (React.createElement("div", { "data-testid": "div-b8449f7f" },
        React.createElement(Card, { "data-testid": "div-8bd80c90", className: "" },
            React.createElement(CardHeader, null,
                React.createElement(CardTitle, { "data-testid": "h3-55b1b911", className: "flex items-center gap-4" },
                    t('SHIPPER_CHARGES'),
                    showDownloadRateCard && renderDownloadLink())),
            React.createElement(CardContent, { "data-testid": "div-ac540022", className: "overflow-x-auto" },
                React.createElement(Table, { "data-testid": "table-5fcbbecd", className: "" },
                    React.createElement(TableHeader, { "data-testid": "thead-686cffab" },
                        React.createElement(TableRow, { "data-testid": "tr-4445a614" },
                            React.createElement(TableHead, { "data-testid": "th-f5f02229", className: "w-48" }, t('CHARGE')),
                            React.createElement(TableHead, { "data-testid": "th-d2210e93", className: "w-20" }, t('COST')),
                            React.createElement(TableHead, { "data-testid": "th-b772e62b", className: "w-40" }, t('ENTERED_DATE')),
                            React.createElement(TableHead, { "data-testid": "th-036952da", className: "w-40" }, t('RATE_SOURCE')),
                            React.createElement(TableHead, { "data-testid": "th-71b62c17", className: "w-52" }, t('INVOICE', { count: 1 })),
                            React.createElement(TableHead, { "data-testid": "th-6225dddc", className: "w-40" }, t('INVOICE_DATE')),
                            React.createElement(TableHead, { "data-testid": "th-5aa999cf", className: "w-5" }, t('EMAIL_SENT')),
                            React.createElement(TableHead, { "data-testid": "th-11240d0b", className: "w-5" }, t('EDI_SENT')),
                            React.createElement(TableHead, { "data-testid": "th-33b0d070", className: "w-64" }, titleize(t('ACTION'))),
                            React.createElement(TableHead, { "data-testid": "th-eefd8d8d", className: "w-64" }, t('REASON_CODE')),
                            React.createElement(TableHead, { "data-testid": "th-d5b382fa", className: "w-52" }, titleize(t('NOTE', { count: 3 }))),
                            React.createElement(TableHead, { "data-testid": "th-032e6bd5", className: "w-52" }, t('LAST_USER')))),
                    React.createElement(TableBody, { "data-testid": "tbody-7558de9d" },
                        lineItems.map((lineItem, index) => {
                            var _a, _b, _c;
                            return (React.createElement(TableRow, { "data-testid": `tr-ecb27b9a-${index}`, key: lineItem._id },
                                React.createElement(TableCell, { "data-testid": `td-3fd5ddae-${index}`, className: "break-words" },
                                    React.createElement(AdjustmentTitle, { lineItem: lineItem, unapplied: isIntermodalShipmentType && !lineItem.equipment })),
                                React.createElement(TableCell, { "data-testid": `td-ca6fdd9a-${index}` },
                                    React.createElement("span", { "data-testid": `span-edaf2609-${index}`, className: classnames({
                                            'text-red-500': lineItem.value < 0,
                                        }) }, formatMoney(lineItem.value, lineItem === null || lineItem === void 0 ? void 0 : lineItem.currency))),
                                React.createElement(TableCell, { "data-testid": `td-1a061458-${index}` },
                                    React.createElement(DateField, { date: lineItem.timestamp, timezone: timezone })),
                                React.createElement(TableCell, { "data-testid": `td-8fe9d102-${index}`, className: "text-capitalize" }, getRateSource(lineItem)),
                                React.createElement(TableCell, { "data-testid": `td-21f8b5ea-${index}` }, renderInvoiceLink(lineItem)),
                                React.createElement(TableCell, { "data-testid": `td-8e9b2769-${index}` },
                                    React.createElement(DateField, { date: (_a = lineItem.invoice) === null || _a === void 0 ? void 0 : _a.timestamp, timezone: timezone })),
                                React.createElement(TableCell, { "data-testid": `td-ff087fec-${index}` }, ((_b = lineItem.invoice) === null || _b === void 0 ? void 0 : _b.email_sent) && (React.createElement(Tooltip, { dataTestId: `Tooltip-58641052-${index}`, className: "w-5 h-5", body: () => (React.createElement(CircleCheckIcon, { className: "text-green-cm h-5 w-5" })) },
                                    React.createElement("div", { "data-testid": `div-e613b1f7-${index}` }, t('EMAIL_SENT_WITH_EXCLAMATION'))))),
                                React.createElement(TableCell, { "data-testid": `td-7c03eedf-${index}` }, ((_c = lineItem.invoice) === null || _c === void 0 ? void 0 : _c.edi_sent) && (React.createElement(Tooltip, { dataTestId: `Tooltip-668bdc03-${index}`, className: "w-5 h-5", body: () => (React.createElement(CircleCheckIcon, { className: "text-green-cm h-5 w-5" })) },
                                    React.createElement("div", { "data-testid": `div-b371eb12-${index}` }, t('EDI_SENT_WITH_EXCLAMATION'))))),
                                React.createElement(TableCell, { "data-testid": `td-dc9eb3ea-${index}` },
                                    React.createElement("div", { "data-testid": `div-c98938dc-${index}`, className: "flex items-center space-x-2" },
                                        React.createElement(AdjustmentAction, { lineItem: lineItem, shipmentIsCurrent: shipmentIsCurrent, isIntermodalShipmentType: isIntermodalShipmentType, onAddCustomAdjustment: onAddCustomAdjustment, onAddShipperCostAdjustment: (lineItem) => onAddShipperCostAdjustment(lineItem), onAddCustomerAdjustment: onAddCustomerAdjustment, onAddInternalAdjustment: onAddInternalAdjustment }),
                                        showRemoveButton(lineItem) && (React.createElement(Tooltip, { dataTestId: `Tooltip-6934443d-${index}`, id: "remove-line-item", position: "top", body: () => renderRemoveButton(lineItem) }, t('REMOVE_LINE_ITEM'))))),
                                React.createElement(TableCell, { "data-testid": `td-1ccc26da-${index}`, className: "break-all" }, (lineItem === null || lineItem === void 0 ? void 0 : lineItem.reason_code) || ''),
                                React.createElement(TableCell, { "data-testid": `td-8518de51-${index}`, className: "break-words" }, lineItem === null || lineItem === void 0 ? void 0 : lineItem.notes),
                                React.createElement(TableCell, { "data-testid": `td-33e1c994-${index}`, className: "break-words" }, lineItem === null || lineItem === void 0 ? void 0 : lineItem.user)));
                        }),
                        React.createElement(TableRow, { "data-testid": "tr-e8ae2415" },
                            React.createElement(TableCell, { "data-testid": "td-32309f05" },
                                React.createElement("div", { "data-testid": "div-292f3677", className: "w-48" }, t('TOTAL'))),
                            React.createElement(TableCell, { "data-testid": "td-d7126c40", className: "font-bold" }, formatMoney(total, (_a = lineItems[0]) === null || _a === void 0 ? void 0 : _a.currency)),
                            React.createElement(TableCell, { "data-testid": "td-e02b9618", colSpan: 12 }, "\u00A0")))),
                React.createElement(AddBaseCostForm, { ledgerType: "receivables", ledgers: charges, onAddBaseCost: onAddBaseCost, currency: currency }))),
        showDetailsModal && (React.createElement(CargomaticModal, { dataTestId: "CargomaticModal-cfbab181", title: "", show: showDetailsModal, footer: null, body: React.createElement(InvoiceDetails, { invoiceId: selectedLineItem }), onHide: handleHideDetailsModal, dialogClassName: "modal-override", location: "top" })),
        showConfirmRemovalModal && (React.createElement(ConfirmModal, { dataTestId: "ConfirmModal-b07649dc", show: showConfirmRemovalModal, onConfirm: handleConfirmLineItemRemoval, onHide: handleHideConfirmRemovalModal, title: t('APPROVE_ITEM_REMOVAL_TITLE'), message: t('APPROVE_ITEM_REMOVAL_MESSAGE'), affirmative: t('APPROVE'), hideButtonText: t('NO') }))));
}
ShipperCharges.propTypes = {
    shipmentId: PropTypes.string.isRequired,
    shipmentIsCurrent: PropTypes.bool.isRequired,
    timezone: PropTypes.string,
    isIntermodalShipmentType: PropTypes.bool,
};
ShipperCharges.defaultProps = {
    timezone: 'America/Los_Angeles',
};
